import { useMemo } from 'react';
import { useAuthStore } from '../../stores/auth.store';
import { useAttendanceToday, useAttendanceHistory } from '../../hooks/useAttendance';
import { useMyDevices } from '../../hooks/useDevices';
import { WorkTimerCard } from '../../components/dashboard/WorkTimerCard';
import { AttendanceOverview } from '../../components/dashboard/AttendanceOverview';
import { InstallAppCard } from '../../components/dashboard/InstallAppCard';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  working: { label: 'Tracking now', color: '#0f9d58' },
  on_break: { label: 'On break', color: '#7b3dff' },
  not_started: { label: 'Paused', color: '#8b93a7' },
  ended: { label: 'Paused', color: '#8b93a7' },
};

function todayDateKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const { data: today } = useAttendanceToday();
  const { data: myDevices, isLoading: loadingMyDevices } = useMyDevices();
  const hasNoDevice = !loadingMyDevices && (myDevices?.length ?? 0) === 0;

  // First real clock-in of the day (not just the current open session's
  // start, which would be wrong after a break/resume) — derived from
  // today's actual attendance_sessions rows, not a client-side guess.
  const dateKey = todayDateKey();
  const { data: todayHistory } = useAttendanceHistory({
    userId: user?.id,
    startDate: dateKey,
    endDate: dateKey,
    limit: 20,
  });

  const firstClockInAt = useMemo(() => {
    interface SessionRow {
      type: string;
      clockInAt: string;
    }
    const workSessions = ((todayHistory?.data ?? []) as SessionRow[]).filter(
      (s) => s.type === 'work'
    );
    if (workSessions.length === 0) return null;
    return workSessions.reduce((earliest, s) =>
      new Date(s.clockInAt) < new Date(earliest.clockInAt) ? s : earliest
    ).clockInAt;
  }, [todayHistory]);

  const status = today?.status ?? 'not_started';
  const statusConfig = STATUS_CONFIG[status] ?? STATUS_CONFIG.not_started;

  const dateLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="flex flex-col gap-[26px]">
      <header className="flex items-end justify-between gap-5 flex-wrap">
        <div>
          <div className="text-[29px] font-extrabold tracking-[-0.8px] text-[#0d1220]">Overview</div>
          <div className="text-[13px] text-[#8b93a7] mt-[3px]">{dateLabel} · Today</div>
        </div>
        <div
          className="flex items-center gap-2 bg-white border border-[#e8eaf2] rounded-full px-3.5 py-2 text-[12.5px] font-bold"
          style={{ color: statusConfig.color }}
        >
          <span
            className="status-pulse-dot w-2 h-2 rounded-full"
            style={{ background: statusConfig.color }}
          />
          {statusConfig.label}
        </div>
      </header>

      {hasNoDevice && <InstallAppCard />}

      <section className="grid gap-[22px] items-stretch [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
        <WorkTimerCard firstClockInAt={firstClockInAt} />
        <AttendanceOverview userId={user?.id} />
      </section>
    </div>
  );
}
